import { Platform, AspectRatio } from '../types';

/**
 * Platform metadata and configuration
 */

export const PLATFORM_CONFIG = {
  facebook: {
    name: 'Facebook',
    icon: '🔷',
    color: '#1877F2',
    aspectRatio: '4:5' as AspectRatio,
    outputWidth: 1080,
    outputHeight: 1350,
  },
  meta: {
    name: 'Meta',
    icon: '🔷',
    color: '#0081FB',
    aspectRatio: '4:5' as AspectRatio,
    outputWidth: 1080,
    outputHeight: 1350,
  },
  instagram: {
    name: 'Instagram',
    icon: '📷',
    color: '#E4405F',
    aspectRatio: '1:1' as AspectRatio,
    outputWidth: 1080,
    outputHeight: 1080,
  },
  google_ads: {
    name: 'Google Ads',
    icon: '🔍',
    color: '#4285F4',
    aspectRatio: '16:9' as AspectRatio,
    outputWidth: 1200,
    outputHeight: 628,
  },
  google_search: {
    name: 'Google Search',
    icon: '🔍',
    color: '#4285F4',
    aspectRatio: 'auto' as AspectRatio,
    outputWidth: 1200,
    outputHeight: 628,
  },
  google_display: {
    name: 'Google Display',
    icon: '🖼️',
    color: '#4285F4',
    aspectRatio: '16:9' as AspectRatio,
    outputWidth: 1200,
    outputHeight: 628,
  },
  google_shopping: {
    name: 'Google Shopping',
    icon: '🛒',
    color: '#4285F4',
    aspectRatio: '1:1' as AspectRatio,
    outputWidth: 1200,
    outputHeight: 1200,
  },
  youtube: {
    name: 'YouTube',
    icon: '📺',
    color: '#FF0000',
    aspectRatio: '16:9' as AspectRatio,
    outputWidth: 1280,
    outputHeight: 720,
  },
  tiktok: {
    name: 'TikTok',
    icon: '🎵',
    color: '#000000',
    aspectRatio: '9:16' as AspectRatio,
    outputWidth: 1080,
    outputHeight: 1920,
    safeZone: { top: 0.15, bottom: 0.20, left: 0.05, right: 0.05 },
  },
  linkedin: {
    name: 'LinkedIn',
    icon: '💼',
    color: '#0A66C2',
    aspectRatio: '16:9' as AspectRatio,
    outputWidth: 1200,
    outputHeight: 627,
  },
  pinterest: {
    name: 'Pinterest',
    icon: '📌',
    color: '#E60023',
    aspectRatio: '9:16' as AspectRatio,
    outputWidth: 1000,
    outputHeight: 1500,
    safeZone: { top: 0.10, bottom: 0.15, left: 0.05, right: 0.05 },
  },
  twitter: {
    name: 'X (Twitter)',
    icon: '𝕏',
    color: '#000000',
    // X's in-feed link ad is 1.91:1 — matches the website card spec and the
    // dimensions image_processor.py renders for this platform.
    aspectRatio: '1.91:1' as AspectRatio,
    outputWidth: 1200,
    outputHeight: 628,
  },
};

export const getPlatformName = (platform: Platform): string => {
  return PLATFORM_CONFIG[platform]?.name || platform;
};

export const getPlatformIcon = (platform: Platform): string => {
  return PLATFORM_CONFIG[platform]?.icon || '📱';
};

export const getPlatformColor = (platform: Platform): string => {
  return PLATFORM_CONFIG[platform]?.color || '#000000';
};

export const getPlatformAspectRatio = (platform: Platform): AspectRatio => {
  return PLATFORM_CONFIG[platform]?.aspectRatio || 'auto';
};

export const getPlatformOutputDimensions = (platform: Platform): { width: number; height: number } => {
  const cfg = PLATFORM_CONFIG[platform];
  return cfg ? { width: cfg.outputWidth, height: cfg.outputHeight } : { width: 1200, height: 628 };
};

export interface SafeZone { top: number; bottom: number; left: number; right: number; }

export const getPlatformSafeZone = (platform: Platform): SafeZone | null => {
  const cfg = PLATFORM_CONFIG[platform] as Record<string, unknown>;
  return (cfg?.safeZone as SafeZone) ?? null;
};

/** Iterable list of all platforms for dropdown selectors */
export const PLATFORM_LIST = Object.entries(PLATFORM_CONFIG).map(
  ([key, cfg]) => ({ key: key as Platform, ...cfg })
);
